import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';

import { fetchIceServers } from '@/lib/iceServers';
import { VoiceSignaling, type SdpType } from '@/lib/voiceSignaling';
import type { GpsPosition } from '@/types/geo';

// Last-resort fallback if the backend `/ice-servers` call fails. Same-network connections
// (LAN, friendly NAT) still work via host/srflx; symmetric-NAT cases will fail without TURN.
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];

// Location-share cadence. ~2.5s keeps even a large group at trivial signaling traffic while
// staying well inside the map's 10s staleness window (several missed sends before grey-out).
const LOCATION_SEND_MS = 2500;

interface Peer {
  pc: RTCPeerConnection;
  audioEl: HTMLAudioElement;
  pendingIce: RTCIceCandidateInit[];
  remoteDescSet: boolean;
  name: string;
  /** Whether *we* have silenced this peer's incoming audio locally (their <audio> muted). */
  muted: boolean;
}

export type VoiceStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

/** A remote rider in the channel, as surfaced to the UI. */
export interface VoicePeer {
  id: string;
  name: string;
  /** True if we've locally silenced this peer's incoming audio (local-only, not signaled). */
  muted: boolean;
}

/** Last-known position of a remote rider in the channel (Phase 5 — location sharing). */
export interface PeerLocation {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /** m/s, as reported by their Geolocation API; null when unknown. */
  speed: number | null;
  /** Degrees clockwise from north; null when unknown. Kept (with speed) so a future
   *  iteration can dead-reckon riders between updates and through dropouts. */
  heading: number | null;
  /** Local clock (ms) when this update arrived — drives the staleness grey-out. */
  receivedAt: number;
}

/**
 * A route another rider pushed to the channel — shown as a banner. Saved routes arrive
 * as an id to fetch (M2); unsaved ones (planner result, loaded GPX) arrive with their
 * geometry inline as an encoded polyline — exactly one of the two is set.
 */
export interface SharedRouteNotice {
  fromName: string;
  routeName: string;
  receivedAt: number;
  /** Saved-route share: fetch `GET /routes/{id}` to view. */
  routeId?: string;
  /** Inline share: the (client-thinned) geometry itself; nothing exists server-side. */
  polyline?: string;
  durationSeconds?: number | null;
}

export interface UseVoiceResult {
  status: VoiceStatus;
  peers: VoicePeer[];
  /** Last-known positions of the other riders in the channel, for the map. */
  peerLocations: PeerLocation[];
  /** Whether the local mic is muted (audio tracks disabled). */
  muted: boolean;
  error: string | null;
  /** The latest route pushed to the channel by another rider, until dismissed/viewed. */
  sharedRoute: SharedRouteNotice | null;
  dismissSharedRoute: () => void;
  join: (channel: string) => Promise<void>;
  leave: () => Promise<void>;
  toggleMute: () => void;
  /** Silence/unsilence a single peer's incoming audio, for this listener only. */
  togglePeerMute: (peerId: string) => void;
  /** Pushes a saved route to the channel. False when not currently in one. */
  shareRoute: (routeId: string, routeName: string) => Promise<boolean>;
  /** Pushes an unsaved route's geometry to the channel. Throws on a hub rejection (too
   *  large); false when not currently in a channel. */
  shareRouteInline: (routeName: string, polyline: string, durationSeconds: number | null) => Promise<boolean>;
  /**
   * Feed the latest GPS fix in. Cheap ref write — safe to call on every position tick.
   * While in a channel, the hook shares the most recent fix on a steady interval.
   */
  reportPosition: (position: GpsPosition) => void;
}

function useVoiceState(): UseVoiceResult {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [peers, setPeers] = useState<VoicePeer[]>([]);
  const [peerLocations, setPeerLocations] = useState<PeerLocation[]>([]);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sharedRoute, setSharedRoute] = useState<SharedRouteNotice | null>(null);

  const signalingRef = useRef<VoiceSignaling | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const peersRef = useRef<Map<string, Peer>>(new Map());
  const peerLocationsRef = useRef<Map<string, PeerLocation>>(new Map());
  // During a reconnect re-join: peers not yet re-announced by the server. Whatever is
  // left when the re-join completes left the channel during our outage and gets dropped.
  const rejoinPruneRef = useRef<Set<string> | null>(null);
  // Latest GPS fix from the map, shared on the send interval below.
  const positionRef = useRef<GpsPosition | null>(null);
  // Fetched once per session in `join`, reused for every new RTCPeerConnection.
  const iceServersRef = useRef<RTCIceServer[]>(FALLBACK_ICE_SERVERS);

  const refreshPeers = useCallback(() => {
    setPeers(Array.from(peersRef.current, ([id, peer]) => ({ id, name: peer.name, muted: peer.muted })));
  }, []);

  const refreshPeerLocations = useCallback(() => {
    setPeerLocations(Array.from(peerLocationsRef.current.values()));
  }, []);

  const createPeer = useCallback(
    (peerId: string, displayName: string): Peer => {
      const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });

      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      document.body.appendChild(audioEl);

      const localStream = localStreamRef.current;
      if (localStream) {
        localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));
      }

      pc.ontrack = (event) => {
        console.log('[voice] ontrack from', peerId, 'streams:', event.streams.length);
        audioEl.srcObject = event.streams[0];
        // Defensive: autoplay can be blocked even after a user gesture in some contexts
        void audioEl.play().catch((err) => console.warn('[voice] audio.play() blocked', err));
      };

      pc.onconnectionstatechange = () => {
        console.log('[voice] connection state for', peerId, ':', pc.connectionState);
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && signalingRef.current) {
          void signalingRef.current.sendIce(peerId, JSON.stringify(event.candidate.toJSON()));
        }
      };

      const peer: Peer = { pc, audioEl, pendingIce: [], remoteDescSet: false, name: displayName, muted: false };
      peersRef.current.set(peerId, peer);
      refreshPeers();
      return peer;
    },
    [refreshPeers],
  );

  const removePeer = useCallback(
    (peerId: string) => {
      // Location is dropped before the early return: a peer can have reported a position
      // without us holding an RTCPeerConnection for it yet (PeerLocation can outrun
      // PeerJoined the same way an offer can), and PeerLeft must not leak a ghost dot.
      if (peerLocationsRef.current.delete(peerId)) {
        refreshPeerLocations();
      }
      const peer = peersRef.current.get(peerId);
      if (!peer) return;
      peer.pc.close();
      peer.audioEl.srcObject = null;
      peer.audioEl.remove();
      peersRef.current.delete(peerId);
      refreshPeers();
    },
    [refreshPeers, refreshPeerLocations],
  );

  const handlePeerJoined = useCallback(
    async (peerId: string, displayName: string, shouldCreateOffer: boolean) => {
      rejoinPruneRef.current?.delete(peerId);

      // Peer ids are user ids — stable across reconnects. A PeerJoined for a peer we
      // already hold is the server replaying the member list after our re-join: the
      // RTCPeerConnection is still alive on both ends, so recover with an ICE restart
      // on it instead of tearing down (no audio gap, no ghost eviction).
      const existing = peersRef.current.get(peerId);
      if (existing) {
        existing.name = displayName;
        refreshPeers();
        if (!shouldCreateOffer) return;
        try {
          const offer = await existing.pc.createOffer({ iceRestart: true });
          await existing.pc.setLocalDescription(offer);
          if (signalingRef.current && offer.sdp) {
            await signalingRef.current.sendSdp(peerId, offer.sdp, 'offer');
          }
        } catch (err) {
          console.warn('[voice] ICE restart offer failed', err);
        }
        return;
      }

      const peer = createPeer(peerId, displayName);
      if (!shouldCreateOffer) return;
      try {
        const offer = await peer.pc.createOffer();
        await peer.pc.setLocalDescription(offer);
        if (signalingRef.current && offer.sdp) {
          await signalingRef.current.sendSdp(peerId, offer.sdp, 'offer');
        }
      } catch (err) {
        console.warn('[voice] offer failed', err);
      }
    },
    [createPeer, refreshPeers],
  );

  const handleReceiveSdp = useCallback(
    async (fromPeerId: string, sdp: string, sdpType: SdpType) => {
      let peer = peersRef.current.get(fromPeerId);
      if (!peer) {
        // An offer can outrun its PeerJoined notification: during two simultaneous joins
        // the server's announcement to us can be lost (we may not be in the SignalR group
        // yet when it's broadcast), but the offer is sent direct to our connection id.
        // Don't drop it — create the peer; the name is cosmetic and arrives next join.
        if (sdpType !== 'offer') return;
        peer = createPeer(fromPeerId, 'Rider');
      }
      try {
        await peer.pc.setRemoteDescription({ type: sdpType, sdp });
        peer.remoteDescSet = true;

        // Race fix: ICE candidates can arrive before setRemoteDescription resolves.
        // We queue them in handleReceiveIce; flush here after the remote desc is set.
        for (const candidate of peer.pendingIce) {
          try {
            await peer.pc.addIceCandidate(candidate);
          } catch (err) {
            console.warn('[voice] flushed ICE candidate failed', err);
          }
        }
        peer.pendingIce = [];

        if (sdpType === 'offer') {
          const answer = await peer.pc.createAnswer();
          await peer.pc.setLocalDescription(answer);
          if (signalingRef.current && answer.sdp) {
            await signalingRef.current.sendSdp(fromPeerId, answer.sdp, 'answer');
          }
        }
      } catch (err) {
        console.warn('[voice] handleReceiveSdp failed', err);
      }
    },
    [createPeer],
  );

  const handlePeerLocation = useCallback(
    (
      fromPeerId: string,
      name: string,
      lat: number,
      lon: number,
      speed: number | null,
      heading: number | null,
    ) => {
      peerLocationsRef.current.set(fromPeerId, {
        id: fromPeerId,
        // The name now rides with the location, so the dot is labelled correctly even
        // before (or without) the WebRTC voice peer being established. Fall back to the
        // voice peer's name, then a generic label, only if the server ever sends an empty.
        name: name || peersRef.current.get(fromPeerId)?.name || 'Rider',
        lat,
        lon,
        speed,
        heading,
        receivedAt: Date.now(),
      });
      refreshPeerLocations();
    },
    [refreshPeerLocations],
  );

  const handleRouteShared = useCallback(
    (fromPeerId: string, riderName: string, routeId: string, routeName: string) => {
      // Latest share wins — one banner, not a queue: on a group ride the newest route is
      // the relevant one. The receiver fetches the authoritative route by id on "View".
      setSharedRoute({
        fromName: riderName || peersRef.current.get(fromPeerId)?.name || 'Rider',
        routeId,
        routeName,
        receivedAt: Date.now(),
      });
    },
    [],
  );

  const handleRouteSharedInline = useCallback(
    (fromPeerId: string, riderName: string, routeName: string, polyline: string, durationSeconds: number | null) => {
      // Same latest-wins single banner as id shares; "View" decodes the polyline locally.
      setSharedRoute({
        fromName: riderName || peersRef.current.get(fromPeerId)?.name || 'Rider',
        routeName,
        polyline,
        durationSeconds,
        receivedAt: Date.now(),
      });
    },
    [],
  );

  const dismissSharedRoute = useCallback(() => setSharedRoute(null), []);

  const shareRoute = useCallback(async (routeId: string, routeName: string): Promise<boolean> => {
    const signaling = signalingRef.current;
    if (!signaling) return false;
    try {
      await signaling.shareRoute(routeId, routeName);
      return true;
    } catch (err) {
      console.warn('[voice] shareRoute failed', err);
      return false;
    }
  }, []);

  const shareRouteInline = useCallback(
    async (routeName: string, polyline: string, durationSeconds: number | null): Promise<boolean> => {
      const signaling = signalingRef.current;
      if (!signaling) return false;
      // Unlike shareRoute, hub rejections propagate: the backstop size check throws a
      // HubException whose message ("save it to your library first") the button shows.
      await signaling.shareRouteInline(routeName, polyline, durationSeconds);
      return true;
    },
    [],
  );

  const handleReceiveIce = useCallback(async (fromPeerId: string, candidateJson: string) => {
    const peer = peersRef.current.get(fromPeerId);
    if (!peer) return;
    let candidate: RTCIceCandidateInit;
    try {
      candidate = JSON.parse(candidateJson) as RTCIceCandidateInit;
    } catch (err) {
      console.warn('[voice] malformed ICE candidate from', fromPeerId, err);
      return;
    }
    if (peer.remoteDescSet) {
      try {
        await peer.pc.addIceCandidate(candidate);
      } catch (err) {
        console.warn('[voice] addIceCandidate failed', err);
      }
    } else {
      peer.pendingIce.push(candidate);
    }
  }, []);

  /** Close every peer connection but keep the mic stream and signaling connection alive. */
  const clearPeers = useCallback(() => {
    for (const peerId of Array.from(peersRef.current.keys())) {
      removePeer(peerId);
    }
    // Locations of peers without an RTCPeerConnection entry aren't covered by the loop above.
    if (peerLocationsRef.current.size > 0) {
      peerLocationsRef.current.clear();
      refreshPeerLocations();
    }
  }, [removePeer, refreshPeerLocations]);

  const tearDownAll = useCallback(() => {
    clearPeers();
    rejoinPruneRef.current = null;
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    signalingRef.current = null;
    setMuted(false);
  }, [clearPeers]);

  const handleReconnecting = useCallback(() => {
    // Signaling is down but the WebRTC mesh is peer-to-peer — it may still be carrying
    // audio through a pure signaling blip. Keep the peers; just surface the degraded state.
    setStatus('reconnecting');
  }, []);

  const handleReconnected = useCallback(() => {
    // The signaling wrapper is about to replay the channel join. Peers are keyed by user
    // id, so every existing RTCPeerConnection is still valid — keep them all and mark
    // them as awaiting re-announcement; the replayed PeerJoined events turn into ICE
    // restarts, and whatever isn't re-announced left during the outage (pruned on
    // rejoin completion).
    rejoinPruneRef.current = new Set(peersRef.current.keys());
  }, []);

  const handleRejoined = useCallback(() => {
    const missing = rejoinPruneRef.current;
    rejoinPruneRef.current = null;
    if (missing) {
      for (const peerId of missing) {
        removePeer(peerId);
      }
    }
    setStatus('connected');
  }, [removePeer]);

  const handleReconnectFailed = useCallback(
    (err: unknown) => {
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
      const signaling = signalingRef.current;
      tearDownAll();
      if (signaling) {
        void signaling.leave().catch((e) => console.warn('[voice] leave after failed reconnect', e));
      }
    },
    [tearDownAll],
  );

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    setMuted((prev) => {
      const next = !prev;
      // Disabling a track keeps the connection up but sends silence — exactly what mute should do.
      stream.getAudioTracks().forEach((t) => (t.enabled = !next));
      return next;
    });
  }, []);

  const reportPosition = useCallback((position: GpsPosition) => {
    positionRef.current = position;
  }, []);

  // Location send loop: while in a channel, share the latest fix every LOCATION_SEND_MS.
  // A timer (rather than sending on each GPS tick) guarantees a steady cadence even when
  // watchPosition goes quiet on a stationary rider — peers' staleness grey-out depends on
  // silence meaning "actually unreachable", not "stopped for fuel".
  useEffect(() => {
    if (status !== 'connected' && status !== 'reconnecting') return;

    const send = () => {
      const position = positionRef.current;
      const signaling = signalingRef.current;
      if (!position || !signaling) return;
      // Geolocation reports NaN heading (and speed on some platforms) when stationary —
      // normalise to null; the hub drops NaN coordinates as garbage.
      const speed = position.speed != null && !Number.isNaN(position.speed) ? position.speed : null;
      const heading = position.heading != null && !Number.isNaN(position.heading) ? position.heading : null;
      signaling
        .sendLocation(position.lat, position.lon, speed, heading)
        .catch((err) => console.warn('[voice] location send failed', err));
    };

    // Announce immediately on join so others see us before the first interval tick.
    send();
    const id = setInterval(send, LOCATION_SEND_MS);
    return () => clearInterval(id);
  }, [status]);

  const togglePeerMute = useCallback(
    (peerId: string) => {
      const peer = peersRef.current.get(peerId);
      if (!peer) return;
      peer.muted = !peer.muted;
      // Mutes playback of this peer's <audio> for us only — nothing is signaled to them.
      peer.audioEl.muted = peer.muted;
      refreshPeers();
    },
    [refreshPeers],
  );

  const join = useCallback(
    async (channel: string): Promise<void> => {
      if (status === 'connecting' || status === 'connected' || status === 'reconnecting') return;
      setStatus('connecting');
      setError(null);

      try {
        // Mic + ICE servers in parallel: both block joining, neither depends on the other.
        // Mic must succeed (or there's nothing to send). ICE fetch can fail gracefully.
        const [stream, iceServersResult] = await Promise.allSettled([
          navigator.mediaDevices.getUserMedia({ audio: true }),
          fetchIceServers(),
        ]);

        if (stream.status === 'rejected') {
          throw stream.reason;
        }
        localStreamRef.current = stream.value;

        if (iceServersResult.status === 'fulfilled') {
          iceServersRef.current = iceServersResult.value;
        } else {
          console.warn('[voice] /ice-servers fetch failed, falling back to public STUN:', iceServersResult.reason);
          iceServersRef.current = FALLBACK_ICE_SERVERS;
        }

        signalingRef.current = new VoiceSignaling({
          onPeerJoined: handlePeerJoined,
          onPeerLeft: removePeer,
          onReceiveSdp: handleReceiveSdp,
          onReceiveIce: handleReceiveIce,
          onPeerLocation: handlePeerLocation,
          onRouteShared: handleRouteShared,
          onRouteSharedInline: handleRouteSharedInline,
          onReconnecting: handleReconnecting,
          onReconnected: handleReconnected,
          onRejoined: handleRejoined,
          onReconnectFailed: handleReconnectFailed,
        });

        await signalingRef.current.join(channel);
        setStatus('connected');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setStatus('error');
        // Capture before tearDownAll nulls the ref: the connection may have started even
        // though the join failed, and must be stopped or it lingers and retries forever.
        const signaling = signalingRef.current;
        tearDownAll();
        if (signaling) {
          void signaling.leave().catch((e) => console.warn('[voice] cleanup after failed join', e));
        }
      }
    },
    [
      status,
      handlePeerJoined,
      removePeer,
      handleReceiveSdp,
      handleReceiveIce,
      handlePeerLocation,
      handleRouteShared,
      handleRouteSharedInline,
      handleReconnecting,
      handleReconnected,
      handleRejoined,
      handleReconnectFailed,
      tearDownAll,
    ],
  );

  const leave = useCallback(async (): Promise<void> => {
    const signaling = signalingRef.current;
    tearDownAll();
    if (signaling) {
      try {
        await signaling.leave();
      } catch (err) {
        console.warn('[voice] leave failed', err);
      }
    }
    setStatus('idle');
    setError(null);
  }, [tearDownAll]);

  useEffect(() => {
    return () => {
      const signaling = signalingRef.current;
      tearDownAll();
      if (signaling) {
        void signaling.leave();
      }
    };
    // Intentionally empty deps — only clean up on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    peers,
    peerLocations,
    muted,
    error,
    sharedRoute,
    dismissSharedRoute,
    join,
    leave,
    toggleMute,
    togglePeerMute,
    shareRoute,
    shareRouteInline,
    reportPosition,
  };
}

const VoiceContext = createContext<UseVoiceResult | null>(null);

/**
 * Owns the voice/location session for the logged-in (or guest) surface. A context rather
 * than a per-component hook because two distant components share the session: VoiceControls
 * in the bottom bar drives join/leave/mute, while MapView feeds positions in and renders
 * the other riders' dots.
 */
export const VoiceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const voice = useVoiceState();
  return <VoiceContext.Provider value={voice}>{children}</VoiceContext.Provider>;
};

export function useVoice(): UseVoiceResult {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoice must be used within a VoiceProvider');
  return ctx;
}
